<template>
  <div class="position-absolute list" :style="{ left: `${left}px` }" v-if="listItems.length > 0">
    <div
      class="list-item selectable pointer"
      :class="{ selected: index === 0 }"
      :data-id="item.id.toString()"
      v-for="(item, index) in listItems"
      :key="item.id"
      @mousedown="emitSelected(item.id)"
    >
      <user-short
        v-if="meta.edge === 'values' && fieldType === FieldType.USER"
        :user-id="item.id"
        :length="20"
        :show-user-name="true"
      />
      <kr-list-item-template
        :option="item"
        v-else-if="meta.edge === 'values' && fieldType === FieldType.KEY_RESULT"
      />
      <span v-else>{{ item.label }}</span>
    </div>
  </div>
</template>

<script>
import KrListItemTemplate from '@/components/conditionSelector/KrListItemTemplate.vue'
import UserShort from '@/components/UserShort.vue'
import { CUSTOM_FIELD_TYPE } from '@/constants/customFields'
import { FIELD_NQL_OPTIONS_MAP, FieldType, InitiativeFieldId } from '@/constants/initiative/filter'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import isFieldACustomField from '@/utils/initiative/IsFieldACustomField'
import { mapActions } from 'vuex'

/**
 * This shows the list the options users can select to build NQL expression
 */
export default {
  name: 'AutoCompleteList',
  props: ['left', 'filterConfiguration', 'orgId', 'subOrgId', 'meta'],
  mixins: [ListNavigationMixin],
  components: {
    UserShort,
    KrListItemTemplate
  },
  data() {
    return {
      FieldType,
      goals: null
    }
  },
  computed: {
    searchText() {
      return this.meta.value
    },
    fieldType() {
      const field = this.meta.field
      if (isFieldACustomField(field)) {
        const fieldDefinition = this.getCustomFieldDefinition(field)
        if (fieldDefinition.type === CUSTOM_FIELD_TYPE.USER) return FieldType.USER
      }
      return this.filterConfiguration.fields[field]
    },
    allKRs() {
      return this.goals
        ?.map(goal => goal.keyResults)
        .flat()
        .map(kr => ({
          id: kr.id,
          goalId: kr.goalId,
          label: kr.title
        }))
    },
    allTeams() {
      return this.$store.getters.userTeamsByOrgIdSubOrgId(this.orgId, this.subOrgId).map(team => ({
        id: team.id,
        label: team.name
      }))
    },
    allUsers() {
      return this.$store.getters.teamMemberships(this.subOrgId).map(({ user }) => ({
        label: user.displayName,
        id: user.id,
        searchField: user.email
      }))
    },
    boolOptions() {
      return [{ id: true, label: 'true' }, { id: false, label: 'false' }]
    },
    listItems() {
      let options = []
      if (this.meta.edge === 'field') {
        const systemFieldOptions = [
          { id: InitiativeFieldId.OWNER, label: 'Owner' },
          { id: InitiativeFieldId.TEAM, label: 'Team' },
          { id: InitiativeFieldId.CREATED_AT, label: 'Created At' },
          { id: InitiativeFieldId.UPDATED_AT, label: 'Updated At' },
          { id: InitiativeFieldId.DUE_DATE, label: 'Due date' },
          { id: InitiativeFieldId.STATUS, label: 'Status' },
          { id: InitiativeFieldId.PRIORITY, label: 'Priority' },
          { id: InitiativeFieldId.KEY_RESULT, label: 'Key Result' },
          { id: InitiativeFieldId.COMMENT_COUNT, label: 'Comment Count' },
          { id: InitiativeFieldId.IS_CLOSED, label: 'Resolution' },
          { id: InitiativeFieldId.CLOSED_ON, label: 'Closed On' },
          { id: InitiativeFieldId.LAST_COMMENT_ON, label: 'Last Comment On' },
          { id: InitiativeFieldId.LAST_UPDATED_BY, label: 'Last Updated By' },
          { id: InitiativeFieldId.LAST_COMMENT_UPDATE_ON, label: 'Last Comment Update On' },
          { id: InitiativeFieldId.LAST_MENTION_ON, label: 'Last Mention On' }
        ]
        const customFieldOptions = this.orgCFs.map(field => ({
          id: `cf_${field.id}`,
          label: field.name,
          type: field.type
        }))
        options = [...systemFieldOptions, ...customFieldOptions]
      } else if (this.meta.edge === 'values') {
        const field = this.meta.field
        const fieldType = this.filterConfiguration.fields[field]
        options = FIELD_NQL_OPTIONS_MAP[field]
        if (!options) {
          if (isFieldACustomField(field)) {
            const fieldDefinition = this.getCustomFieldDefinition(field)
            if (fieldDefinition.type === CUSTOM_FIELD_TYPE.DROPDOWN) {
              options = fieldDefinition.dropdownOptions.map(option => ({
                ...option,
                label: option.name
              }))
            } else if (fieldDefinition.type === CUSTOM_FIELD_TYPE.USER) {
              options = this.allUsers
            }
          } else if (fieldType === 'user') {
            options = this.allUsers
          } else if (fieldType === FieldType.KEY_RESULT) {
            options = this.allKRs
          } else if (field === 'team') {
            options = this.allTeams
          } else if (fieldType === FieldType.BOOL) {
            options = this.boolOptions
          }
        }
      }
      const searchTerm = this.searchText?.toLowerCase()
      return (options || []).filter(
        x =>
          x.label.toLowerCase().startsWith(searchTerm) ||
          x.searchField?.toLowerCase().startsWith(searchTerm)
      )
    },
    orgCFs() {
      return this.$store.state.orgCustomFields[this.orgId] || []
    }
  },
  methods: {
    ...mapActions(['getGoals']),
    getCustomFieldDefinition(field) {
      const fieldId = getIdFromCFPrefixedField(field)
      return this.orgCFs.find(field => field.id === fieldId)
    },
    async fetchGoals() {
      this.goals = await this.getGoals({
        orgId: this.orgId,
        options: {
          isArchived: false,
          scopeId: this.subOrgId
        }
      })
    },
    emitSelected(id) {
      this.$emit('select', id)
    }
  },
  async mounted() {
    if (this.meta.field === FieldType.KEY_RESULT && this.meta.edge === 'values') {
      this.fetchGoals()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.list {
  top: 45px;
  transition: left ease-in 100ms;
  box-shadow: $menuShadow;
  background: $documentColor;
  z-index: 110;
  border-radius: 0.25rem;
  max-height: 450px;
  overflow: auto;
  min-width: 180px;
  max-width: 350px;
}

.list-item {
  padding: 0.25rem 1.5rem;

  &:hover,
  &.selected {
    background-color: $quinary;
  }
}
</style>
